import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import HighlightText from '../components/HighlightText';
import TutorProfileModal from '../components/TutorProfileModal';
import { allTutors } from '../data/tutorsData';
import { slugToLabel, locations } from '../data/locationsData';

const {
  FiCheckCircle, FiMapPin, FiPhone, FiPhoneCall, FiBookOpen,
  FiUsers, FiFilter, FiStar, FiBook, FiUser, FiX,
  FiShield, FiAward, FiMessageCircle,
} = FiIcons;

const BADGE_STYLES = {
  'IIT Expert': 'bg-blue-600 text-white',
  'NEET Expert': 'bg-green-600 text-white',
};

const LeadForm = ({ locationLabel }) => {
  const [form, setForm] = useState({ name: '', phone: '', subject: '', timing: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.phone.trim()) errs.phone = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(form.phone.trim())) errs.phone = 'Enter a valid 10-digit number';
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    const message =
      `Hello MyHomeTutors! I'm looking for a home tutor in *${locationLabel}*, Lucknow.%0A%0A` +
      `👤 *Name:* ${encodeURIComponent(form.name.trim())}%0A` +
      `📞 *Phone:* ${encodeURIComponent(form.phone.trim())}%0A` +
      `📚 *Class & Subject:* ${encodeURIComponent(form.subject.trim() || 'Not specified')}%0A` +
      `🕐 *Preferred Timing:* ${encodeURIComponent(form.timing.trim() || 'Flexible')}%0A%0A` +
      `Please arrange a *free demo class* at the earliest.`;
    const url = `https://wa.me/919415905584?text=${message}`;
    const newTab = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
  };

  const fields = [
    { name: 'name', placeholder: 'Parent / Student Name', icon: FiUsers },
    { name: 'phone', placeholder: 'Mobile Number', icon: FiPhoneCall },
    { name: 'subject', placeholder: 'Class & Subject (e.g., 10th Math)', icon: FiBookOpen },
    { name: 'timing', placeholder: 'Preferred Timing (e.g., Evening)', icon: FiIcons.FiClock },
  ];

  return (
    <div className="bg-white shadow-2xl border border-red-100 overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-primary via-orange-400 to-yellow-400" />
      <div className="p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-50 flex items-center justify-center">
            <FiMessageCircle className="text-green-600 w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-slate-900">Book a Free Demo Class</h3>
            <p className="text-xs text-gray-500 font-medium mt-0.5">We'll reply on WhatsApp instantly</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} noValidate className="space-y-3">
          {fields.map((field) => (
            <div key={field.name}>
              <div className="relative">
                <field.icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={field.name === 'phone' ? 'tel' : 'text'}
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  maxLength={field.name === 'phone' ? 10 : undefined}
                  className={`w-full pl-11 pr-4 py-3 border focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50 text-slate-700 text-sm placeholder-gray-400 transition font-medium ${errors[field.name] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                />
              </div>
              {errors[field.name] && <p className="text-xs text-red-500 font-semibold mt-1 pl-1">{errors[field.name]}</p>}
            </div>
          ))}
          <button type="submit" className="w-full bg-primary text-white py-4 font-extrabold hover:bg-primary-hover transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform text-base mt-2 flex items-center justify-center gap-2 tracking-wide">
            <FiPhoneCall className="w-4 h-4" />
            Request Free Callback
          </button>
        </form>
        <div className="mt-4 flex items-center justify-center gap-3">
          <div className="flex -space-x-2">
            {[
              'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=64&q=80',
              'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=64&q=80',
              'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=64&q=80',
            ].map((src, i) => <img key={i} className="w-6 h-6 rounded-full border-2 border-white" src={src} alt="user" />)}
          </div>
          <p className="text-xs text-gray-500 font-medium">
            <span className="font-bold text-slate-800">500+</span> parents in {locationLabel} trust us
          </p>
        </div>
      </div>
    </div>
  );
};

const TrustBadge = ({ icon: Icon, text, sub }) => (
  <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-4 py-3 border border-white/60 shadow-sm">
    <div className="w-9 h-9 bg-red-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <p className="text-sm font-extrabold text-slate-900">{text}</p>
      {sub && <p className="text-xs text-gray-500 font-medium">{sub}</p>}
    </div>
  </div>
);

const FilterGroup = ({ title, options, selected, setter }) => {
  const toggle = (val) => setter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  return (
    <div className="mb-5 pb-5 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
      <h4 className="font-extrabold text-slate-800 text-xs mb-3 uppercase tracking-wider">{title}</h4>
      <div className="space-y-2 max-h-44 overflow-y-auto pr-1 custom-scrollbar">
        {options.map((opt, idx) => {
          const isSelected = selected.includes(opt);
          return (
            <button key={idx} onClick={() => toggle(opt)} className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-all text-sm font-medium ${isSelected ? 'bg-red-50 text-primary border border-red-200' : 'hover:bg-gray-50 text-slate-600 border border-transparent'}`}>
              <div className={`w-4 h-4 flex-shrink-0 border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-primary border-primary' : 'border-gray-300'}`}>
                {isSelected && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="truncate flex-1">{opt}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LocationTutorCard = ({ tutor, onViewProfile }) => (
  <div className="bg-white p-5 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col">
    <div className="flex gap-4 mb-4">
      <div className="relative flex-shrink-0">
        <img src={tutor.img} alt={tutor.name} className="w-[72px] h-[72px] object-cover border-2 border-red-100 shadow-sm" />
        {tutor.verified && (
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 flex items-center justify-center border-2 border-white">
            <FiCheckCircle className="text-white w-3 h-3 fill-current" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <h4 className="font-extrabold text-slate-900 text-base tracking-tight truncate">{tutor.name}</h4>
          {tutor.badge && (
            <span className={`flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${BADGE_STYLES[tutor.badge] || 'bg-primary text-white'}`}>
              <FiAward className="w-2.5 h-2.5" />{tutor.badge}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 font-semibold mt-0.5 truncate">{tutor.qualifications}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs bg-blue-50 text-blue-600 font-bold px-2 py-0.5">{tutor.exp}</span>
          <span className="text-xs bg-gray-100 text-gray-600 font-bold px-2 py-0.5">{tutor.gender}</span>
        </div>
        <div className="flex items-center gap-0.5 mt-1.5">
          {[...Array(5)].map((_, i) => <FiStar key={i} className="w-3 h-3 fill-current text-yellow-400" />)}
        </div>
      </div>
    </div>
    <div className="space-y-1.5 mb-4 text-sm">
      <div className="flex items-center gap-2">
        <FiBook className="text-primary w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold text-slate-700 truncate">{tutor.subjects.join(', ')}</span>
      </div>
      <div className="flex items-center gap-2">
        <FiUser className="text-primary w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold text-slate-600 truncate">{tutor.classes.join(', ')}</span>
      </div>
      <div className="flex items-center gap-2">
        <FiMapPin className="text-primary w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold text-slate-600 truncate">{tutor.locations.join(', ')}</span>
      </div>
    </div>
    <div className="flex items-center gap-2 mt-auto">
      <button onClick={() => onViewProfile(tutor)} className="flex-1 bg-gray-50 text-slate-700 border border-gray-200 py-2.5 text-sm font-extrabold hover:bg-gray-100 transition text-center">
        View Profile
      </button>
      <a href="tel:+919415905584" className="flex-1 bg-primary text-white py-2.5 text-sm font-extrabold hover:bg-primary-hover transition shadow-sm text-center flex items-center justify-center gap-1.5">
        <FiPhone className="w-3.5 h-3.5" />Book Demo
      </a>
    </div>
  </div>
);

/* ── Alambagh SEO Content Block ── */
const AlamabaghSEOContent = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { q: 'How much does home tuition cost in Alambagh, Lucknow?', a: 'Home tuition fees in Alambagh start from approximately ₹2,500/month for primary classes and can go up to ₹6,000+/month for Class 11–12 or competitive exam coaching. All pricing is discussed upfront with no hidden costs.' },
    { q: 'Is the demo class free in Alambagh?', a: 'Yes — your first class with any tutor in Alambagh is completely free. We encourage parents to observe the session too, so you can make a fully informed decision.' },
    { q: 'What boards do your Alambagh tutors cover?', a: 'Our tutors in Alambagh are experienced across CBSE, ICSE, UP Board (Hindi & English medium), and NIOS — covering all major school boards in the Lucknow region.' },
    { q: 'How does the hybrid IIT JEE / NEET coaching work in Alambagh?', a: 'Students can choose offline home sessions for concept building and practice, combined with scheduled online classes for doubt-clearing, mock tests, and revision — giving them the benefits of both formats without having to travel to a distant coaching centre.' },
  ];

  const whyUs = [
    { icon: FiShield, title: '100% Verified Teachers', desc: 'Safety is non-negotiable. Every tutor serving Alambagh is ID-verified, address-confirmed, and academically screened before being listed on our platform. Our 15+ years of trust across Lucknow is built on this rigorous verification standard.', color: 'bg-blue-50 text-blue-600' },
    { icon: FiIcons.FiClock, title: 'Quick 24-Hour Matching', desc: 'We know your child\'s learning can\'t wait. Once you share your requirement, our team works immediately to deliver matched tutor profiles within 24 hours — aligned to your child\'s class, board, subject gap, and preferred schedule in Alambagh.', color: 'bg-green-50 text-green-600' },
    { icon: FiIcons.FiDollarSign, title: 'Affordable Pricing', desc: 'Great home tutoring in Alambagh is now accessible to every family. Our budget-friendly pricing structure is fully transparent — no hidden registration fees, no surprise charges, and flexible plans that suit different household budgets without compromising on tutor quality.', color: 'bg-orange-50 text-orange-600' },
  ];

  const subjects = [
    { icon: FiBookOpen, title: 'Classes 1 to 8 (Primary & Middle School)', desc: 'Building strong basics early makes all the difference. Our tutors in Alambagh focus on concept clarity, reading fluency, and problem-solving skills for students in Classes 1–8. Daily homework help, school exam prep, and subject-specific coaching ensure your child stays ahead — not just afloat.', color: 'bg-purple-50 text-purple-600' },
    { icon: FiAward, title: 'Classes 9 to 12 (High School & Board Exams)', desc: 'With CBSE and UP Board exams determining future pathways, our board exam specialists in Alambagh deliver intensive one-on-one coaching in Maths, Physics, Chemistry, Biology, English, Accounts, and Economics. Structured revision plans and chapter-wise mock tests keep students consistently on track.', color: 'bg-red-50 text-primary' },
    { icon: FiIcons.FiTarget, title: 'IIT JEE & NEET Preparation (Online & Offline)', desc: 'Alambagh students dreaming of IITs and top medical colleges can now access premium IIT JEE and NEET coaching at home or online. Our expert tutors offer both offline in-home sessions and live online classes — providing the perfect balance of structured learning and scheduling flexibility throughout the year.', color: 'bg-indigo-50 text-indigo-600' },
  ];

  const steps = [
    { num: '01', title: 'Submit Your Requirement', desc: 'Call or WhatsApp us with your child\'s class, subject, board, and preferred time slot' },
    { num: '02', title: 'Get Matched in 24 Hours', desc: 'We send you handpicked, verified tutor profiles suited to Alambagh and your child\'s needs' },
    { num: '03', title: 'Take a FREE Demo Class', desc: 'Sit in on a trial session, evaluate the tutor with zero pressure, and confirm only when satisfied' },
  ];

  return (
    <section className="bg-white py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-5 leading-tight">
            Expert Home Tutors & Exam Coaching in <span className="text-primary">Alambagh</span>
          </h1>
          <p className="text-gray-600 font-medium leading-relaxed text-base">
            Find the best home tutor in Alambagh, Lucknow — verified, experienced, and ready to teach at your home. MyHomeTutors.org has helped thousands of Lucknow families since 2009 with personalised 1-on-1 home tuition in Alambagh for all classes, all boards, and all competitive exams including IIT JEE and NEET. Our promise: the right tutor at your door within 24 hours and a FREE first demo class — no strings attached.
          </p>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Why Choose Our Tutors in <span className="text-primary">Alambagh?</span>
          </h2>
          <p className="text-gray-500 font-medium mb-8 text-sm leading-relaxed">
            Alambagh is one of Lucknow's most active residential and commercial hubs — and home to thousands of students who deserve focused, quality education. Here's why MyHomeTutors is Alambagh's first choice for home tuition:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyUs.map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-extrabold text-slate-900 mb-2 text-base">{item.title}</h3>
                <p className="text-sm text-gray-500 font-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Academic Subjects & <span className="text-primary">Competitive Exams</span>
          </h2>
          <p className="text-gray-500 font-medium mb-8 text-sm">
            Our tutors in Alambagh are qualified across all levels — from foundational primary schooling to elite entrance exam coaching.
          </p>
          <div className="space-y-5">
            {subjects.map((sub, i) => (
              <div key={i} className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${sub.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <sub.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 mb-2 text-base">{sub.title}</h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">{sub.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
            Start in <span className="text-primary">3 Simple Steps</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative bg-gradient-to-br from-[#fff5f5] to-[#ffeaea] rounded-2xl p-6 border border-red-100">
                <div className="text-5xl font-extrabold text-red-100 mb-3 leading-none">{step.num}</div>
                <h3 className="font-extrabold text-slate-900 mb-2 text-base">{step.title}</h3>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`border rounded-2xl overflow-hidden transition-all ${openFaq === i ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-white'}`}>
                <button className="w-full text-left px-6 py-4 flex items-center justify-between gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-extrabold text-slate-800 text-sm">{faq.q}</span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === i ? 'bg-primary text-white' : 'bg-red-50 text-primary'}`}>
                    <FiIcons.FiChevronDown className={`w-4 h-4 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-48 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-gray-600 font-medium text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Aashiana SEO Content Block ── */
const AashianaSEOContent = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { q: 'How much does home tuition cost in Aashiana, Lucknow?', a: 'Home tuition fees in Aashiana depend on the class level, subject, and number of sessions per week. Typically, fees start from ₹2,500/month for primary classes and range up to ₹6,000+/month for Class 11–12 or JEE/NEET coaching. A precise quote is shared upfront — no hidden costs involved.' },
    { q: 'Is the demo class really free for students in Aashiana?', a: 'Absolutely. Every student in Aashiana receives one completely free demo session with their matched tutor. There is no payment required and no pressure to continue — the demo is purely for you to evaluate the tutor\'s teaching style and your child\'s comfort level.' },
    { q: 'Which school boards do your Aashiana tutors teach?', a: 'Our tutors in Aashiana are well-versed in all major boards — CBSE, ICSE, UP Board (both Hindi and English medium), and NIOS — covering every curriculum followed by schools in and around the Aashiana area.' },
    { q: 'How does online and offline IIT JEE / NEET coaching work in Aashiana?', a: 'Students can opt for offline in-home sessions for regular concept-building and practice, complemented by scheduled online classes for live doubt-clearing, mock tests, and topic revision. This hybrid format gives students in Aashiana the best of both worlds — structured personal coaching without the need to travel to a distant coaching institute.' },
  ];

  const whyUs = [
    { icon: FiShield, title: '100% Verified Teachers', desc: 'Every tutor we place in Aashiana homes is thoroughly vetted — government ID verified, address confirmed, and academic qualifications cross-checked before approval. We also assess each tutor\'s actual teaching ability, communication skills, and subject expertise. You\'re not just getting a degree holder — you\'re getting a proven educator your child can trust.', color: 'bg-blue-50 text-blue-600' },
    { icon: FiIcons.FiClock, title: 'Quick 24-Hour Matching', desc: 'Time matters when your child is falling behind or preparing for an exam. Submit your requirement — class, subject, board, timing preference — and our team personally reviews it to deliver 2–3 perfectly matched tutor profiles right to your phone within just 24 hours. No generic lists. No guesswork.', color: 'bg-green-50 text-green-600' },
    { icon: FiIcons.FiDollarSign, title: 'Affordable Pricing', desc: 'We believe outstanding home tuition in Aashiana should be within every family\'s reach. Our fee structure is completely upfront — what we quote is what you pay. No enrollment fees, no monthly add-ons, no surprises. Just quality teaching at honest, budget-friendly rates.', color: 'bg-orange-50 text-orange-600' },
  ];

  const subjects = [
    { icon: FiBookOpen, title: 'Classes 1 to 8 (Primary & Middle School)', desc: 'Early years set the tone for a child\'s entire academic journey. Our Aashiana tutors for primary and middle school focus on building genuine understanding — not rote memorisation. Whether it\'s Maths tables, Hindi grammar, English reading, or Science concepts, we make learning enjoyable and rooted in clarity. Homework support, weekly revision, and school test preparation are all part of every session.', color: 'bg-purple-50 text-purple-600' },
    { icon: FiAward, title: 'Classes 9 to 12 (High School & Board Exams)', desc: 'The board exam years are high-pressure — and our tutors in Aashiana are specifically trained to handle that pressure with students. From CBSE to UP Board, we offer intensive subject coaching in Mathematics, Physics, Chemistry, Biology, English Literature, Accountancy, and Economics. Each student gets a personalised study schedule, chapter-wise practice sessions, and regular performance reviews to ensure consistent progress leading up to the exams.', color: 'bg-red-50 text-primary' },
    { icon: FiIcons.FiTarget, title: 'IIT JEE & NEET Preparation (Online & Offline)', desc: 'For students in Aashiana with ambitions of cracking IIT JEE or NEET, we offer dedicated entrance exam coaching — available both as in-home offline sessions and live interactive online classes. Our expert faculty specialise in breaking down high-difficulty topics in Physics, Chemistry, Maths, and Biology into structured, step-by-step lessons. Whether your child is starting foundation prep in Class 9 or in the final stretch of Class 12, we have the right programme for every stage.', color: 'bg-indigo-50 text-indigo-600' },
  ];

  const steps = [
    { num: '01', title: 'Submit Your Requirement', desc: 'Call or WhatsApp us with your child\'s class, subject, preferred board, and available time slots in Aashiana' },
    { num: '02', title: 'Get Matched in 24 Hours', desc: 'Receive handpicked, fully verified tutor profiles best suited to your child\'s learning needs and your locality' },
    { num: '03', title: 'Take a FREE Demo Class', desc: 'Attend a no-obligation trial session, assess the tutor\'s approach firsthand, and confirm only when you\'re fully confident' },
  ];

  return (
    <section className="bg-white py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-5 leading-tight">
            Expert Home Tutors & Exam Coaching in <span className="text-primary">Aashiana</span>
          </h1>
          <p className="text-gray-600 font-medium leading-relaxed text-base">
            Find the most trusted home tutor in Aashiana, Lucknow — background-verified, highly qualified, and available at your doorstep. At MyHomeTutors.org, we have been simplifying quality education for Lucknow families since 2009 with dedicated 1-on-1 home tuition in Aashiana — covering every class, every board, and every competitive exam including IIT JEE and NEET. Tell us your requirement today and receive your perfect tutor match within 24 hours, with a completely FREE first demo class — zero risk, zero commitment.
          </p>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Why Choose Our Tutors in <span className="text-primary">Aashiana?</span>
          </h2>
          <p className="text-gray-500 font-medium mb-8 text-sm leading-relaxed">
            Aashiana is a well-planned, family-first residential colony where parents take their children's education seriously. At MyHomeTutors, we match that commitment with a tutoring experience built on safety, speed, and results. Here's what makes us Aashiana's most preferred home tuition service:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyUs.map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-extrabold text-slate-900 mb-2 text-base">{item.title}</h3>
                <p className="text-sm text-gray-500 font-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Academic Subjects & <span className="text-primary">Competitive Exams</span>
          </h2>
          <p className="text-gray-500 font-medium mb-8 text-sm">
            From a Class 1 student learning to read to a Class 12 student chasing a top board score, our tutors in Aashiana are equipped to deliver results at every academic level.
          </p>
          <div className="space-y-5">
            {subjects.map((sub, i) => (
              <div key={i} className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${sub.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <sub.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 mb-2 text-base">{sub.title}</h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">{sub.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
            Start in <span className="text-primary">3 Simple Steps</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative bg-gradient-to-br from-[#fff5f5] to-[#ffeaea] rounded-2xl p-6 border border-red-100">
                <div className="text-5xl font-extrabold text-red-100 mb-3 leading-none">{step.num}</div>
                <h3 className="font-extrabold text-slate-900 mb-2 text-base">{step.title}</h3>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`border rounded-2xl overflow-hidden transition-all ${openFaq === i ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-white'}`}>
                <button className="w-full text-left px-6 py-4 flex items-center justify-between gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-extrabold text-slate-800 text-sm">{faq.q}</span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === i ? 'bg-primary text-white' : 'bg-red-50 text-primary'}`}>
                    <FiIcons.FiChevronDown className={`w-4 h-4 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-48 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-gray-600 font-medium text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Aishbagh SEO Content Block ── */
const AishbaghSEOContent = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { q: 'What is the home tuition fee in Aishbagh, Lucknow?', a: 'Fees in Aishbagh are based on class level, subject, and weekly session frequency. Primary classes typically start from ₹2,000–₹3,000/month, while Class 9–12 and competitive exam coaching ranges from ₹4,000–₹6,500+/month. Full pricing is shared upfront — nothing is hidden.' },
    { q: 'Can I take a free trial class before paying anything in Aishbagh?', a: 'Yes, always. Every new student in Aishbagh is entitled to one free demo class with their matched tutor — no payment, no pressure. You only proceed if the session feels right for your child.' },
    { q: 'Do you have UP Board Hindi-medium tutors available in Aishbagh?', a: 'Yes. We have experienced tutors in Aishbagh who specialise in UP Board Hindi-medium teaching for Classes 6 through 12 across all core subjects — making us a strong choice for families in this part of Lucknow.' },
    { q: 'Is IIT JEE and NEET coaching available both online and at home in Aishbagh?', a: 'Yes. Students in Aishbagh can combine offline home sessions for regular practice and concept clarity with online live classes for doubt-solving and revision — a flexible, results-focused model that eliminates the need to travel to a coaching centre.' },
  ];

  const whyUs = [
    { icon: FiShield, title: '100% Verified Teachers', desc: 'Before any tutor steps into your Aishbagh home, they go through our strict multi-layer verification process — identity check, residential address confirmation, and full academic credential review. We also personally assess their teaching ability and communication style. Every tutor you meet through us has already earned our trust — and is ready to earn yours.', color: 'bg-blue-50 text-blue-600' },
    { icon: FiIcons.FiClock, title: 'Quick 24-Hour Matching', desc: 'We respect your time. Once you share your child\'s details — class, subject, board, and preferred schedule — our team gets to work immediately. You receive carefully shortlisted, verified tutor options suited specifically to your location in Aishbagh within 24 hours. No delays, no random picks.', color: 'bg-green-50 text-green-600' },
    { icon: FiIcons.FiDollarSign, title: 'Affordable Pricing', desc: 'Good education should not feel like a luxury. Our tuition plans in Aishbagh are straightforward and budget-conscious — with clear, honest pricing discussed before any session begins. No surprise bills. No registration charges. Just reliable, affordable home tuition you can plan around.', color: 'bg-orange-50 text-orange-600' },
  ];

  const subjects = [
    { icon: FiBookOpen, title: 'Classes 1 to 8 (Primary & Middle School)', desc: 'The foundation years matter more than most parents realise. Our tutors work patiently with young learners in Aishbagh to strengthen core skills — number sense in Maths, reading comprehension in English and Hindi, and curiosity in Science. Each session is relaxed yet purposeful, with regular homework support and school test prep woven in naturally. Children don\'t just pass — they begin to enjoy learning.', color: 'bg-purple-50 text-purple-600' },
    { icon: FiAward, title: 'Classes 9 to 12 (High School & Board Exams)', desc: 'High school is where academic pressure peaks — and where the right tutor makes the biggest difference. Our Aishbagh tutors for Classes 9 to 12 come with deep subject knowledge and board exam experience across CBSE and UP Board. From cracking tricky Maths problems to writing structured answers in English and scoring well in Science practicals, students get focused, exam-aligned coaching with regular mock tests and honest performance feedback.', color: 'bg-red-50 text-primary' },
    { icon: FiIcons.FiTarget, title: 'IIT JEE & NEET Preparation (Online & Offline)', desc: 'Big entrance exam goals need serious, consistent preparation — and our tutors in Aishbagh are fully equipped for both. Students aiming for IIT JEE or NEET can choose from in-home offline coaching for deep concept work or live online sessions for flexible scheduling and real-time doubt resolution. Our faculty cover Physics, Chemistry, Mathematics, and Biology with a problem-solving approach that builds both speed and accuracy — from early foundation level all the way to final exam readiness.', color: 'bg-indigo-50 text-indigo-600' },
  ];

  const steps = [
    { num: '01', title: 'Submit Your Requirement', desc: 'Drop us a call or WhatsApp message with your child\'s class, subject, board preference, and available timing in Aishbagh' },
    { num: '02', title: 'Get Matched in 24 Hours', desc: 'We review your requirement personally and send back verified, best-fit tutor profiles for your area' },
    { num: '03', title: 'Take a FREE Demo Class', desc: 'Meet the tutor, observe the teaching style, and let your child experience the session — confirm only when you\'re happy' },
  ];

  return (
    <section className="bg-white py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-5 leading-tight">
            Expert Home Tutors & Exam Coaching in <span className="text-primary">Aishbagh</span>
          </h1>
          <p className="text-gray-600 font-medium leading-relaxed text-base">
            Searching for a dependable home tutor in Aishbagh, Lucknow? MyHomeTutors.org brings verified, experienced teachers directly to your home — so your child learns better, faster, and with full confidence. Since 2009, we have been Lucknow's most trusted name in personalised 1-on-1 home tuition, and Aishbagh families are no exception. Whether it's daily school support, board exam preparation, or IIT JEE and NEET coaching, we connect you with the right tutor within 24 hours. And your first demo class? Completely FREE.
          </p>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Why Choose Our Tutors in <span className="text-primary">Aishbagh?</span>
          </h2>
          <p className="text-gray-500 font-medium mb-8 text-sm leading-relaxed">
            Aishbagh is a vibrant, densely populated neighbourhood with a strong sense of community — and an equally strong demand for quality education. Parents here want tutors who are not just qualified on paper, but genuinely invested in their child's growth. That's exactly what we deliver.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyUs.map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-extrabold text-slate-900 mb-2 text-base">{item.title}</h3>
                <p className="text-sm text-gray-500 font-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Academic Subjects & <span className="text-primary">Competitive Exams</span>
          </h2>
          <p className="text-gray-500 font-medium mb-8 text-sm">
            Our tutors in Aishbagh bring expertise across all academic levels — keeping learning structured, practical, and genuinely effective.
          </p>
          <div className="space-y-5">
            {subjects.map((sub, i) => (
              <div key={i} className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-red-100 hover:shadow-md transition-all group">
                <div className={`w-12 h-12 rounded-2xl ${sub.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  <sub.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 mb-2 text-base">{sub.title}</h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">{sub.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
            Start in <span className="text-primary">3 Simple Steps</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative bg-gradient-to-br from-[#fff5f5] to-[#ffeaea] rounded-2xl p-6 border border-red-100">
                <div className="text-5xl font-extrabold text-red-100 mb-3 leading-none">{step.num}</div>
                <h3 className="font-extrabold text-slate-900 mb-2 text-base">{step.title}</h3>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`border rounded-2xl overflow-hidden transition-all ${openFaq === i ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-white'}`}>
                <button className="w-full text-left px-6 py-4 flex items-center justify-between gap-4" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-extrabold text-slate-800 text-sm">{faq.q}</span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === i ? 'bg-primary text-white' : 'bg-red-50 text-primary'}`}>
                    <FiIcons.FiChevronDown className={`w-4 h-4 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-48 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-gray-600 font-medium text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const LocationPage = () => {
  const { slug } = useParams();
  const locationLabel = slugToLabel(slug);
  const isAlambagh = slug === 'alambagh';
  const isAashiana = slug === 'aashiana';
  const isAishbagh = slug === 'aishbagh';
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);

  const locationTutors = useMemo(
    () => allTutors.filter((t) => t.locations.includes(locationLabel)),
    [locationLabel]
  );
  const allSubjects = useMemo(() => [...new Set(locationTutors.flatMap((t) => t.subjects))].sort(), [locationTutors]);
  const allClasses = useMemo(() => [...new Set(locationTutors.flatMap((t) => t.classes))].sort(), [locationTutors]);
  const allGenders = ['Male', 'Female'];

  const hasFilters = selectedSubjects.length > 0 || selectedGenders.length > 0 || selectedClasses.length > 0;
  const clearAll = () => { setSelectedSubjects([]); setSelectedGenders([]); setSelectedClasses([]); };

  const filteredTutors = useMemo(() => {
    return locationTutors.filter((t) => {
      const matchSubj = selectedSubjects.length === 0 || t.subjects.some((s) => selectedSubjects.includes(s));
      const matchGender = selectedGenders.length === 0 || selectedGenders.includes(t.gender);
      const matchClass = selectedClasses.length === 0 || t.classes.some((c) => selectedClasses.includes(c));
      return matchSubj && matchGender && matchClass;
    });
  }, [locationTutors, selectedSubjects, selectedGenders, selectedClasses]);

  const allActive = [
    ...selectedSubjects.map((v) => ({ v, clear: () => setSelectedSubjects((p) => p.filter((x) => x !== v)) })),
    ...selectedGenders.map((v) => ({ v, clear: () => setSelectedGenders((p) => p.filter((x) => x !== v)) })),
    ...selectedClasses.map((v) => ({ v, clear: () => setSelectedClasses((p) => p.filter((x) => x !== v)) })),
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `MyHomeTutors - Home Tutors in ${locationLabel}, Lucknow`,
    description: `Find verified home tutors in ${locationLabel}, Lucknow for all classes and subjects.`,
    url: `https://myhometutors.org/location/${slug}`,
    telephone: '+919415905584',
    address: { '@type': 'PostalAddress', streetAddress: 'Vastu Khand, Gomti Nagar', addressLocality: 'Lucknow', addressRegion: 'Uttar Pradesh', postalCode: '226010', addressCountry: 'IN' },
    areaServed: { '@type': 'Place', name: `${locationLabel}, Lucknow` },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '200' },
  };

  return (
    <div className="min-h-screen bg-[#fdf8f4]">
      <Helmet>
        <title>Best Home Tutors in {locationLabel}, Lucknow | MyHomeTutors</title>
        <meta name="description" content={`Find the best verified home tutors in ${locationLabel}, Lucknow for all classes and subjects. Book a free demo class today.`} />
        <link rel="canonical" href={`https://myhometutors.org/location/${slug}/`} />
        <meta property="og:title" content={`Best Home Tutors in ${locationLabel}, Lucknow | MyHomeTutors`} />
        <meta property="og:description" content={`Find verified home tutors in ${locationLabel}, Lucknow for all classes and subjects. Book a free demo class today.`} />
        <meta property="og:url" content={`https://myhometutors.org/location/${slug}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#fff5f5] via-[#fff8f8] to-[#ffeaea] py-14 lg:py-20 overflow-hidden relative">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-white border border-red-100 text-primary text-xs font-bold px-4 py-2 shadow-sm">
                <FiMapPin className="w-3.5 h-3.5" />
                Serving {locationLabel}, Lucknow
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-[1.15] tracking-tight">
                Find Expert <HighlightText>Home Tutors</HighlightText>
                <br className="hidden md:block" />
                in {locationLabel}
              </h1>
              <p className="text-base text-gray-600 leading-relaxed font-medium max-w-lg">
                Your child deserves more than a classroom. Get <strong>1-on-1 personalized tutoring</strong> at home — from verified, experienced teachers right here in {locationLabel}.
              </p>
              <div className="space-y-2.5">
                {[
                  'Tutors arrive at your doorstep — zero travel needed',
                  'Handpicked & background-verified for your safety',
                  'First demo class is completely FREE',
                  'Flexible timing — morning, evening or weekend',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-slate-800 font-semibold text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <FiIcons.FiCheck className="text-green-600 w-3 h-3 stroke-[3]" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a href="tel:+919415905584" className="flex items-center justify-center gap-2 bg-primary text-white px-7 py-3.5 font-bold hover:bg-primary-hover transition-all shadow-lg tracking-wide">
                  <FiPhoneCall className="w-4 h-4" />Call: +91 9415905584
                </a>
                <a href="https://wa.me/919415905584" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-green-500 text-white px-7 py-3.5 font-bold hover:bg-green-600 transition-all shadow-lg tracking-wide">
                  <FiMessageCircle className="w-4 h-4" />WhatsApp Us
                </a>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <TrustBadge icon={FiShield} text="100% Verified" sub="Background checked tutors" />
                <TrustBadge icon={FiAward} text="15+ Years" sub="Trusted in Lucknow" />
                <TrustBadge icon={FiUsers} text="500+ Tutors" sub="Ready to teach" />
                <TrustBadge icon={FiStar} text="4.9 ★ Rating" sub="Google reviews" />
              </div>
            </div>
            <div className="w-full max-w-md mx-auto lg:ml-auto">
              <LeadForm locationLabel={locationLabel} />
            </div>
          </div>
        </div>
      </section>

      {/* Other Locations */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Browse other areas:</span>
          {locations.filter((l) => l.slug !== slug).slice(0, 6).map((loc) => (
            <Link key={loc.slug} to={`/location/${loc.slug}`} className="text-xs font-bold text-primary bg-red-50 border border-red-100 px-3 py-1 hover:bg-primary hover:text-white transition-colors">
              {loc.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Tutors Section */}
      <section className="py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
            Tutors Available in <HighlightText>{locationLabel}</HighlightText>
          </h2>
          <p className="text-gray-500 font-medium mt-2">{locationTutors.length} verified tutors currently serving this area</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-7 items-start mt-6">
          <button className="lg:hidden w-full bg-white border border-gray-200 p-4 flex items-center justify-between font-bold text-slate-700 shadow-sm" onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}>
            <span className="flex items-center gap-2">
              <FiFilter className="text-primary" />Filters
              {hasFilters && <span className="bg-primary text-white text-xs px-2 py-0.5">{allActive.length}</span>}
            </span>
            <span className="bg-red-50 text-primary px-3 py-1 text-xs font-bold">{filteredTutors.length} Tutors</span>
          </button>

          <div className={`w-full lg:w-64 flex-shrink-0 bg-white p-5 shadow-sm border border-gray-100 ${isMobileFilterOpen ? 'block' : 'hidden lg:block'}`}>
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
              <h3 className="font-extrabold text-slate-900 flex items-center gap-2 text-base">
                <FiFilter className="text-primary" />Filter Tutors
              </h3>
              {hasFilters && <button onClick={clearAll} className="text-xs font-bold text-primary hover:underline">Clear All</button>}
            </div>
            <FilterGroup title="By Subject" options={allSubjects} selected={selectedSubjects} setter={setSelectedSubjects} />
            <FilterGroup title="By Class" options={allClasses} selected={selectedClasses} setter={setSelectedClasses} />
            <FilterGroup title="By Gender" options={allGenders} selected={selectedGenders} setter={setSelectedGenders} />
          </div>

          <div className="flex-1 w-full min-w-0">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-slate-600">
                Showing <span className="text-primary">{filteredTutors.length}</span> tutors
              </span>
            </div>

            {allActive.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {allActive.map(({ v, clear }, i) => (
                  <span key={i} className="flex items-center gap-1.5 bg-red-50 text-primary text-xs font-bold px-3 py-1.5 border border-red-100">
                    {v}<button onClick={clear}><FiX className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}

            {locationTutors.length === 0 ? (
              <div className="bg-white p-10 text-center border border-gray-100">
                <div className="w-16 h-16 bg-red-50 flex items-center justify-center mx-auto mb-3">
                  <FiUser className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-1">No tutors in this area yet</h3>
                <p className="text-gray-500 text-sm font-medium mb-4">Call us and we'll find the best match for you.</p>
                <a href="tel:+919415905584" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-bold text-sm hover:bg-primary-hover transition shadow-md">
                  <FiPhone className="w-4 h-4" />Call Now
                </a>
              </div>
            ) : filteredTutors.length === 0 ? (
              <div className="bg-white p-10 text-center border border-gray-100">
                <div className="w-16 h-16 bg-red-50 flex items-center justify-center mx-auto mb-3">
                  <FiUser className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900 mb-1">No tutors match</h3>
                <p className="text-gray-500 text-sm font-medium">Try adjusting your filters.</p>
                <button onClick={clearAll} className="mt-4 bg-primary text-white px-5 py-2 font-bold text-sm hover:bg-primary-hover transition shadow-md">Reset Filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filteredTutors.map((tutor, idx) => (
                  <LocationTutorCard key={idx} tutor={tutor} onViewProfile={setSelectedTutor} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Location-specific SEO content */}
      {isAlambagh && <AlamabaghSEOContent />}
      {isAashiana && <AashianaSEOContent />}
      {isAishbagh && <AishbaghSEOContent />}

      <TutorProfileModal tutor={selectedTutor} onClose={() => setSelectedTutor(null)} />

      <style dangerouslySetInnerHTML={{
        __html: `.custom-scrollbar::-webkit-scrollbar{width:3px}.custom-scrollbar::-webkit-scrollbar-track{background:#f1f5f9}.custom-scrollbar::-webkit-scrollbar-thumb{background:#cbd5e1}`,
      }} />
    </div>
  );
};

export default LocationPage;